// src/pages/SearchResults.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Country } from "country-state-city";
import { useAuthContext } from "../context";
import Sidebar from "../components/Modal/Sidebar";      // your static sidebar
import SearchModal from "../components/Modal/SearchModal";
import UserCard from "../components/Modal/UserCard";

const normalizeGender = (v) =>
  v === "Man" ? "Male" : v === "Woman" ? "Female" : v;

export default function SearchResults() {
  const { allUsers, fetchAllUsers, user } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loggedInUserId = user?.user_id;
  const isGold = user?.membership_type === "gold" && user?.membership_status;

  const RESULTS_PER_PAGE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  const {
    ageRange: initialAge = [18, 45],
    gender: initialGender = "",
    lookingFor: initialLookingFor = "",
    country: initialCountry = "",
    state: initialState = "",
    city: initialCity = "",
  } = location.state || {};

  // fetch once
  useEffect(() => {
    (async () => {
      try {
        await fetchAllUsers();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchAllUsers]);

  // filter logic
  const matchesCountry = (c) => {
    if (!initialCountry) return true;
    const obj = Country.getCountryByCode(initialCountry);
    return c === initialCountry || c === obj?.name;
  };

  const filtered = allUsers.filter((u) => {
    if (u.user_id === loggedInUserId) return false;
    const age = +u.age;
    if (age < initialAge[0] || age > initialAge[1]) return false;
    if (
      initialLookingFor &&
      u.gender !== normalizeGender(initialLookingFor)
    )
      return false;
    if (
      initialGender &&
      u.interestedIn !== normalizeGender(initialGender)
    )
      return false;
    if (!matchesCountry(u.country)) return false;
    if (initialState && u.state !== initialState) return false;
    if (initialCity && u.city !== initialCity) return false;
    return true;
  });
  
  // sort gold users first
  const sorted = filtered.sort((a, b) => {
    const aIsGold = a.membership_type === "gold" && a.membership_status;
    const bIsGold = b.membership_type === "gold" && b.membership_status;
    return bIsGold - aIsGold; // gold = true first
  });
  

  const paginated = isGold
  ? sorted.slice((currentPage - 1) * RESULTS_PER_PAGE, currentPage * RESULTS_PER_PAGE)
  : sorted.slice(0, 3);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const handleModalSearchUpdate = (q) => {
    localStorage.setItem("searchQuery", JSON.stringify(q));
    setShowModal(false);
    navigate("/search", { state: q });
  };

  return (
    <>
      <Helmet>
      <title>Search Results – SomeOne4u</title>
      <meta
        name="description"
        content="Browse the dates we found for you! Filter by age, gender, country, state, and city to find your perfect match on SomeOne4u."
      />
      <meta
        name="keywords"
        content="SomeOne4u, dating search, match results, find singles, filter profiles"
      />
      <link rel="canonical" href="https://someone4u.com/search" />

      <meta property="og:title" content="Search Results – SomeOne4u" />
      <meta
        property="og:description"
        content="Browse the dates we found for you! Filter by age, gender, country, state, and city to find your perfect match on SomeOne4u."
      />
      <meta property="og:url" content="https://someone4u.com/search" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SomeOne4u" />
    </Helmet>

      <div className="min-h-screen flex bg-gray-100">
        {/* Sidebar stays full-height */}
        <Sidebar onSearch={() => setShowModal(true)} />

        {/* Main area */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-red-400">
                Search Results
              </h1>
              <button
                onClick={() => setShowModal(true)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit this search
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center flex-1">
                <div className="animate-spin h-12 w-12 border-t-4 border-red-400 rounded-full" />
              </div>
            ) : paginated.length === 0 ? (
              <div className="text-center mt-10">
                <img
                  src="/image/no-result.png"
                  alt="No results"
                  className="mx-auto w-64 mb-4"
                />
                <p className="text-gray-700 text-lg font-medium">
                  No members found
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Try changing your preferences or location.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
             {paginated.map((u) => (
  <UserCard key={u.user_id} user={u} onClick={() => navigate(`/user/${u.user_id}`)} />
))}
              </div>
            )}

{isGold && filtered.length > RESULTS_PER_PAGE && (
  <div className="flex justify-center items-center mt-6 space-x-4">
    <button
      onClick={() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setCurrentPage((p) => Math.max(p - 1, 1));
      }}
      disabled={currentPage === 1}
      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
    >
      Previous
    </button>
    <span className="text-gray-700 font-medium">
      Page {currentPage} of {Math.ceil(filtered.length / RESULTS_PER_PAGE)}
    </span>
    <button
      onClick={() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setCurrentPage((p) =>
          p < Math.ceil(filtered.length / RESULTS_PER_PAGE) ? p + 1 : p
        );
      }}
      disabled={currentPage === Math.ceil(filtered.length / RESULTS_PER_PAGE)}
      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
    >
      Next
    </button>
  </div>
)}
            {/* push the upgrade callout to the bottom */}
            {!isGold && (
              <div className="mt-auto text-center">
                <p className="text-gray-700 mb-4 mt-6">
                  Upgrade to <strong>Gold</strong> to unlock all profiles.
                </p>
                <button
                   onClick={() => {
                    navigate("/membership");
                    scrollToTop();
                  }}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-full shadow"
                >
                  Upgrade to Gold
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {showModal && (
        <SearchModal
          initialAge={initialAge}
          initialGender={initialGender}
          initialLookingFor={initialLookingFor}
          initialCountry={initialCountry}
          initialState={initialState}
          initialCity={initialCity}
          onSearchUpdate={handleModalSearchUpdate}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
